import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Send } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  currentPermission,
  enablePushNotifications,
  disablePushNotifications,
  sendTestPush,
} from "../../utils/pushNotifications";

/**
 * Compact admin-header control:
 *  - Renders a small bell button reflecting current permission state
 *  - Click → enables push (asks OS permission), or sends a test if already enabled
 *  - On first mount, if the admin has never seen a prompt, shows a non-intrusive banner
 */
const PushPermissionButton = () => {
  const [permission, setPermission] = useState("default");
  const [busy, setBusy] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const supported = isPushSupported();

  useEffect(() => {
    if (!supported) return;
    setPermission(currentPermission());
    // Auto-show once per admin device until they decide
    const seen = localStorage.getItem("push_prompt_seen");
    if (!seen && currentPermission() === "default") {
      setShowBanner(true);
    }
  }, [supported]);

  const handleEnable = async () => {
    setBusy(true);
    const r = await enablePushNotifications();
    setBusy(false);
    localStorage.setItem("push_prompt_seen", "1");
    setShowBanner(false);
    setPermission(currentPermission());
    if (r.ok) {
      toast.success("تم تفعيل الإشعارات على هذا الجهاز");
      // Send a confirmation push so the admin physically sees one
      try { await sendTestPush(); } catch { /* ignore */ }
    } else if (r.reason === "denied") {
      toast.error("الإذن مرفوض من المتصفح. فعّله من إعدادات الموقع.");
    } else if (r.reason === "unsupported") {
      toast.error("متصفحك لا يدعم الإشعارات. استخدم Chrome.");
    } else if (r.reason === "vapid_missing") {
      toast.error("VAPID غير مهيّأ على الخادم.");
    } else {
      toast.error("فشل تفعيل الإشعارات.");
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    await disablePushNotifications();
    setBusy(false);
    setPermission(currentPermission());
    toast.message("تم إيقاف الإشعارات على هذا الجهاز");
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush();
      toast.success(`أُرسل الإشعار التجريبي (${r?.sent ?? 0} أجهزة)`);
    } catch { toast.error("فشل إرسال الإشعار"); }
    finally { setBusy(false); }
  };

  if (!supported) return null;

  // 3 visual states: granted (BellRing), default (Bell), denied (BellOff)
  const state = permission === "granted" ? "on" : permission === "denied" ? "off" : "default";

  return (
    <>
      <div className="flex items-center gap-1">
        {state === "on" ? (
          <button
            onClick={handleTest}
            disabled={busy}
            data-testid="push-test-btn"
            title="إرسال إشعار تجريبي"
            className="p-2 hover:bg-white/10 rounded-full transition-colors text-[#7ED321]"
          >
            <BellRing className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={handleEnable}
            disabled={busy}
            data-testid="push-enable-btn"
            title={state === "off" ? "الإشعارات معطّلة من المتصفح" : "تفعيل الإشعارات"}
            className={`p-2 hover:bg-white/10 rounded-full transition-colors ${state === "off" ? "text-[#E85D45]" : "text-white"}`}
          >
            {state === "off" ? <BellOff className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
          </button>
        )}
        {state === "on" && (
          <button
            onClick={handleDisable}
            disabled={busy}
            data-testid="push-disable-btn"
            title="إيقاف الإشعارات على هذا الجهاز"
            className="p-1.5 hover:bg-white/10 rounded-full text-white/70 text-xs"
          >
            <span className="text-[10px]">إيقاف</span>
          </button>
        )}
      </div>

      {showBanner && (
        <div
          data-testid="push-banner"
          className="fixed bottom-4 right-4 left-4 sm:left-auto z-[140] max-w-sm bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl shadow-2xl p-4"
          dir="rtl"
        >
          <div className="flex items-start gap-3">
            <div className="bg-white/20 rounded-full p-2 shrink-0">
              <Bell className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-sm">فعّل الإشعارات</p>
              <p className="text-[11px] opacity-90 mt-0.5">
                اسمح للمتصفح ليُرسل إشعارات الطلبات الجديدة (مع اسم الزبون ورقم الفاتورة) حتى لو كان التطبيق مغلقاً.
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleEnable}
                  disabled={busy}
                  data-testid="push-banner-allow"
                  className="bg-[#7ED321] hover:bg-[#6cb91d] text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                >
                  <Send className="w-3 h-3" /> سماح وتفعيل
                </button>
                <button
                  onClick={() => { localStorage.setItem("push_prompt_seen", "1"); setShowBanner(false); }}
                  data-testid="push-banner-dismiss"
                  className="bg-white/10 hover:bg-white/20 text-white text-xs px-3 py-1.5 rounded-full"
                >
                  لاحقاً
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PushPermissionButton;
